import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { format, parseISO } from "date-fns";
import { Crown, Gift, CheckCircle2, Clock, XCircle, RotateCcw, Bell, Share2, Copy } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (iso) => format(parseISO(iso), 'MMM d, yyyy');

export const useMembership = (patientId) => {
  const [data, setData] = useState({ current: null, history: [] });
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    try { const r = await axios.get(`${API}/memberships/patient/${patientId}`); setData(r.data); }
    catch { /* silent */ }
    finally { setLoading(false); }
  }, [patientId]);
  useEffect(() => { load(); }, [load]);
  return { ...data, loading, reload: load };
};

export const useActiveMemberIds = () => {
  const [ids, setIds] = useState(new Set());
  useEffect(() => {
    axios.get(`${API}/memberships/active`).then(r => setIds(new Set(r.data.map(m => m.patient_id)))).catch(() => {});
  }, []);
  return ids;
};

export const EliteBadge = ({ className = '', testId }) => (
  <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[9px] font-semibold border-primary/60 text-primary bg-primary/10 tracking-widest gap-1 align-middle ${className}`} data-testid={testId}>
    <Crown className="w-2.5 h-2.5" />ELITE
  </span>
);

const stateStyle = {
  available: 'border-emerald-500/50 bg-emerald-500/10 text-emerald-300',
  used: 'border-border/40 bg-muted/20 text-muted-foreground',
  upcoming: 'border-primary/30 bg-primary/5 text-primary/80',
  expired: 'border-rose-500/30 bg-rose-500/5 text-rose-300/70 line-through',
};
const StateIcon = ({ s }) => s === 'used' ? <CheckCircle2 className="w-3.5 h-3.5" /> : s === 'available' ? <Gift className="w-3.5 h-3.5" /> : s === 'expired' ? <XCircle className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />;

export const CreditGrid = ({ membership, onRestore, canManage }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="membership-credits">
    {membership.credits.map(c => (
      <div key={c.id} className={`rounded-lg border p-2.5 flex items-start justify-between gap-2 ${stateStyle[c.state]}`} data-testid={`credit-${c.id}`}>
        <div className="min-w-0">
          <p className="text-sm font-medium flex items-center gap-1.5"><StateIcon s={c.state} />{c.label}</p>
          <p className="text-[11px] opacity-80 mt-0.5">
            {c.state === 'used' ? `Used ${fmt(c.used_at)}` : c.state === 'upcoming' ? `Unlocks ${fmt(c.window_start)}` : c.state === 'expired' ? `Expired ${fmt(c.window_end)}` : `Use by ${fmt(c.window_end)}`}
          </p>
        </div>
        {c.state === 'used' && canManage && onRestore && (
          <button type="button" onClick={() => onRestore(c.id)} className="text-[10px] flex items-center gap-1 hover:text-primary" data-testid={`restore-credit-${c.id}`} title="Restore credit (undo redemption)">
            <RotateCcw className="w-3 h-3" />Restore
          </button>
        )}
      </div>
    ))}
  </div>
);

const SellMembershipDialog = ({ open, onClose, patient, onSold }) => {
  const [plan, setPlan] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [startDate, setStartDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) axios.get(`${API}/memberships/plan`).then(r => setPlan(r.data)).catch(() => {}); }, [open]);

  const submit = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/memberships`, { patient_id: patient.id, payment_method: paymentMethod, start_date: startDate, notes: notes || null });
      toast.success(`Elite Membership activated · $${plan?.price?.toFixed(2)} collected (${paymentMethod})`);
      onSold(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not activate membership'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="sell-membership-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Crown className="w-5 h-5 text-primary" />Elite Membership</DialogTitle>
          <DialogDescription>{patient?.first_name} {patient?.last_name} · full amount collected today.</DialogDescription>
        </DialogHeader>
        <div className="rounded-xl border border-primary/40 bg-gradient-to-br from-primary/15 to-transparent p-4">
          <div className="flex items-baseline justify-between">
            <p className="text-xs uppercase tracking-[0.2em] text-primary">3-month plan</p>
            <p className="text-3xl font-bold gold-gradient-text" data-testid="membership-price">${plan ? plan.price.toFixed(0) : '450'}</p>
          </div>
          <ul className="text-sm mt-3 space-y-1 text-foreground/90">
            <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-primary shrink-0" />1 IV drip every month for 3 months</li>
            <li className="flex gap-2"><Gift className="w-4 h-4 text-primary shrink-0" />Bonus IV drip + IM injection after the term (valid 30 days)</li>
            <li className="flex gap-2 text-muted-foreground text-xs"><Clock className="w-4 h-4 shrink-0" />Monthly credits don't roll over · mobile fee still applies per visit</li>
          </ul>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label className="text-xs">Start date</Label><Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} data-testid="membership-start-date" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Paid via</Label>
            <Select value={paymentMethod} onValueChange={setPaymentMethod}><SelectTrigger data-testid="membership-payment-method"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="card">Card</SelectItem><SelectItem value="cash">Cash</SelectItem><SelectItem value="external">External terminal / link</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent>
            </Select>
          </div>
        </div>
        <div className="space-y-1.5"><Label className="text-xs">Notes (optional)</Label><Textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)} data-testid="membership-notes" /></div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-primary text-primary-foreground" data-testid="membership-confirm-btn"><Crown className="w-4 h-4 mr-1" />{saving ? 'Saving…' : `Collect $${plan ? plan.price.toFixed(0) : '450'} & Activate`}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const ReferralCard = ({ membership }) => {
  const [data, setData] = useState(null);
  useEffect(() => { if (membership?.id) axios.get(`${API}/memberships/${membership.id}/referral`).then(r => setData(r.data)).catch(() => {}); }, [membership?.id]);
  if (!data) return null;
  const copy = async () => { try { await navigator.clipboard.writeText(data.link); toast.success('Referral link copied'); } catch { toast.error('Copy failed — select the link manually'); } };
  const statusLabel = { booked: 'Visit booked', rewarded: 'Reward earned ✓', completed_no_reward: 'Completed (promo was off)' };
  return (
    <div className={`rounded-xl border p-4 space-y-3 ${data.promo.active ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-border/50 bg-muted/10'}`} data-testid="referral-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] flex items-center gap-1.5 text-primary"><Share2 className="w-4 h-4" />Refer a friend</p>
          <p className="text-sm mt-1 text-muted-foreground">Friend gets <b className="text-foreground">${Number(data.promo.friend_discount).toFixed(0)} off</b> their first visit · member earns a <b className="text-foreground">free IM injection</b> once the friend checks out.</p>
        </div>
        <Badge variant="outline" className={`text-[9px] tracking-widest ${data.promo.active ? 'border-emerald-500/60 text-emerald-300' : 'border-border/60 text-muted-foreground'}`} data-testid="referral-promo-status">
          {data.promo.active ? 'PROMO ON' : 'PROMO PAUSED'}
        </Badge>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <code className="text-lg font-bold tracking-[0.3em] text-primary bg-black/40 px-3 py-1 rounded" data-testid="referral-code">{data.code}</code>
        <Input readOnly value={data.link} className="flex-1 min-w-[220px] text-xs" onFocus={e => e.target.select()} data-testid="referral-link" />
        <Button size="sm" variant="outline" className="border-primary/40" onClick={copy} data-testid="referral-copy-btn"><Copy className="w-3.5 h-3.5 mr-1" />Copy</Button>
      </div>
      {!data.promo.active && <p className="text-[11px] text-muted-foreground">The link works, but discounts and rewards are only granted while the promotion is switched on (Admin → Dashboard → Elite Members).</p>}
      {data.referrals.length > 0 && (
        <div className="space-y-1" data-testid="referral-list">
          <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Referrals · {data.rewards_earned} reward{data.rewards_earned === 1 ? '' : 's'} earned</p>
          {data.referrals.map(r => (
            <div key={r.id} className="flex items-center justify-between text-xs rounded-md border border-border/40 px-3 py-1.5" data-testid={`referral-row-${r.id}`}>
              <span><b>{r.friend_name}</b> · {format(parseISO(r.start_datetime), 'MMM d, h:mm a')}</span>
              <span className={r.status === 'rewarded' ? 'text-emerald-300' : 'text-muted-foreground'}>{statusLabel[r.status] || r.status}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const ReferralPromoToggle = () => {
  const [promo, setPromo] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { axios.get(`${API}/memberships/referral-promo`).then(r => setPromo(r.data)).catch(() => {}); }, []);
  if (!promo) return null;
  const toggle = async () => {
    setSaving(true);
    try { const r = await axios.put(`${API}/memberships/referral-promo`, { active: !promo.active, friend_discount: promo.friend_discount, ends_at: promo.ends_at || null }); setPromo(r.data); toast.success(r.data.active ? 'Referral promotion is ON — members can share their links' : 'Referral promotion paused'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };
  return (
    <button type="button" onClick={toggle} disabled={saving} data-testid="referral-promo-toggle"
      className={`h-7 px-2.5 rounded-full border text-xs flex items-center gap-1.5 transition-colors ${promo.active ? 'border-emerald-500/60 bg-emerald-500/15 text-emerald-300' : 'border-border/50 text-muted-foreground hover:border-primary/50'}`}>
      <Share2 className="w-3 h-3" />Referral promo: <b>{promo.active ? 'ON' : 'OFF'}</b> · ${Number(promo.friend_discount).toFixed(0)} off
    </button>
  );
};

export const MembershipRevenueReport = () => {
  const [data, setData] = useState(null);
  const [months, setMonths] = useState(6);
  useEffect(() => { axios.get(`${API}/memberships/revenue`, { params: { months } }).then(r => setData(r.data)).catch(() => {}); }, [months]);
  if (!data) return null;
  const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  const max = Math.max(1, ...data.months.map(m => m.new_revenue + m.renewal_revenue));
  const t = data.totals;
  return (
    <div className="rounded-xl border border-primary/30 bg-black/30 p-4 space-y-3" data-testid="membership-revenue-report">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-xs uppercase tracking-[0.2em] text-primary flex items-center gap-1.5"><Crown className="w-4 h-4" />Membership revenue</p>
        <div className="flex items-center gap-1">
          {[3, 6, 12].map(n => <button key={n} type="button" onClick={() => setMonths(n)} data-testid={`revenue-range-${n}`} className={`text-[11px] px-2 py-0.5 rounded-full border ${months === n ? 'border-primary bg-primary/15 text-primary' : 'border-border/50 text-muted-foreground hover:border-primary/50'}`}>{n} mo</button>)}
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
        <div className="rounded-lg bg-primary/10 border border-primary/30 p-2"><p className="text-[10px] uppercase tracking-wider text-muted-foreground">Total sales</p><p className="text-xl font-bold text-primary" data-testid="revenue-total">{money(t.total_revenue)}</p><p className="text-[10px] text-muted-foreground">{t.new_count + t.renewal_count} membership{t.new_count + t.renewal_count === 1 ? '' : 's'}</p></div>
        <div className="rounded-lg bg-muted/20 border border-border/40 p-2"><p className="text-[10px] uppercase tracking-wider text-muted-foreground">New</p><p className="text-xl font-bold" data-testid="revenue-new">{money(t.new_revenue)}</p><p className="text-[10px] text-muted-foreground">{t.new_count} sold</p></div>
        <div className="rounded-lg bg-muted/20 border border-border/40 p-2"><p className="text-[10px] uppercase tracking-wider text-muted-foreground">Renewals</p><p className="text-xl font-bold text-emerald-300" data-testid="revenue-renewals">{money(t.renewal_revenue)}</p><p className="text-[10px] text-muted-foreground">{t.renewal_count} renewed</p></div>
        <div className="rounded-lg bg-muted/20 border border-border/40 p-2"><p className="text-[10px] uppercase tracking-wider text-muted-foreground">Discounts given</p><p className="text-xl font-bold text-sky-300" data-testid="revenue-discounts">{money(t.credit_value + t.referral_discounts)}</p><p className="text-[10px] text-muted-foreground">{t.credits_redeemed} credits · {money(t.referral_discounts)} referral</p></div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid="revenue-table">
          <thead><tr className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border/40">
            <th className="text-left py-1.5 font-medium">Month</th><th className="text-left py-1.5 font-medium w-[30%]">Sales</th><th className="text-right py-1.5 font-medium">New</th><th className="text-right py-1.5 font-medium">Renewals</th><th className="text-right py-1.5 font-medium">Credits used</th><th className="text-right py-1.5 font-medium">Referral $</th>
          </tr></thead>
          <tbody>
            {data.months.map(m => (
              <tr key={m.month} className="border-b border-border/20" data-testid={`revenue-row-${m.month}`}>
                <td className="py-1.5 font-medium">{m.label}</td>
                <td className="py-1.5"><div className="h-2 rounded-full bg-muted/30 overflow-hidden flex"><div className="bg-primary h-full" style={{ width: `${(m.new_revenue / max) * 100}%` }} /><div className="bg-emerald-400 h-full" style={{ width: `${(m.renewal_revenue / max) * 100}%` }} /></div></td>
                <td className="py-1.5 text-right">{m.new_count ? `${m.new_count} · ${money(m.new_revenue)}` : <span className="text-muted-foreground/50">—</span>}</td>
                <td className="py-1.5 text-right text-emerald-300">{m.renewal_count ? `${m.renewal_count} · ${money(m.renewal_revenue)}` : <span className="text-muted-foreground/50">—</span>}</td>
                <td className="py-1.5 text-right">{m.credits_redeemed ? `${m.credits_redeemed} · ${money(m.credit_value)}` : <span className="text-muted-foreground/50">—</span>}</td>
                <td className="py-1.5 text-right text-sky-300">{m.referral_discounts ? money(m.referral_discounts) : <span className="text-muted-foreground/50">—</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[10px] text-muted-foreground"><span className="inline-block w-2 h-2 rounded-full bg-primary mr-1" />New memberships <span className="inline-block w-2 h-2 rounded-full bg-emerald-400 ml-3 mr-1" />Renewals · "Credits used" is the retail value of IV/IM credits redeemed at checkout</p>
    </div>
  );
};

export const MembersPanel = ({ data, onOpenPatient, canRemind }) => {
  const [running, setRunning] = useState(false);
  if (!data) return null;
  const remindNow = async () => {
    setRunning(true);
    try { const r = await axios.post(`${API}/memberships/reminders/run`); toast.success(r.data.sent ? `Sent ${r.data.sent} reminder${r.data.sent === 1 ? '' : 's'}` : 'No reminders due right now (sent within 7 days of expiry, once per credit)'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to send reminders'); }
    finally { setRunning(false); }
  };
  return (
    <div className="rounded-xl border border-primary/30 bg-gradient-to-r from-primary/10 via-transparent to-transparent p-4 space-y-3" data-testid="members-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-xs uppercase tracking-[0.2em] text-primary flex items-center gap-1.5"><Crown className="w-4 h-4" />Elite Members</p>
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <span><b className="text-foreground" data-testid="members-active-count">{data.active_members}</b> active</span>
          <span><b className="text-foreground" data-testid="members-credits-available">{data.credits_available}</b> credits usable now</span>
          <span><b className={data.credits_expiring_soon ? 'text-amber-300' : 'text-foreground'} data-testid="members-expiring-count">{data.credits_expiring_soon}</b> expiring within 30 days</span>
          {data.in_bonus_period > 0 && <span><b className="text-emerald-300">{data.in_bonus_period}</b> in bonus period</span>}
          {canRemind && <ReferralPromoToggle />}
          {canRemind && <Button size="sm" variant="outline" className="h-7 text-xs border-primary/40" onClick={remindNow} disabled={running} data-testid="members-remind-now-btn"><Bell className="w-3 h-3 mr-1" />Send due reminders</Button>}
        </div>
      </div>
      {data.referrals && (data.referrals.total_booked > 0 || data.referrals.promo_active) && (
        <div className="rounded-lg border border-border/50 bg-black/20 p-2.5 space-y-1.5" data-testid="referral-leaderboard">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-1.5"><Share2 className="w-3 h-3 text-primary" />Referral program</p>
            <p className="text-[11px] text-muted-foreground">
              <b className="text-foreground" data-testid="referral-new-patients">{data.referrals.new_patients}</b> new patient{data.referrals.new_patients === 1 ? '' : 's'} · <b className="text-foreground" data-testid="referral-completed">{data.referrals.completed}</b> completed visit{data.referrals.completed === 1 ? '' : 's'} · ${Number(data.referrals.discounts_given).toFixed(0)} in friend discounts
            </p>
          </div>
          {data.referrals.top_members.length === 0 ? (
            <p className="text-xs text-muted-foreground">No referrals yet — members can share their link from the Membership tab.</p>
          ) : (
            <ol className="space-y-1" data-testid="referral-top-members">
              {data.referrals.top_members.map((m, i) => (
                <li key={m.patient_id}>
                  <button type="button" onClick={() => onOpenPatient?.(m.patient_id)} data-testid={`referral-leader-${m.patient_id}`} className="w-full flex items-center justify-between gap-2 text-sm text-left hover:bg-primary/5 rounded px-1 py-0.5 transition-colors">
                    <span className="flex items-center gap-2"><span className={`w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center ${i === 0 ? 'bg-primary text-black' : 'bg-muted/40 text-muted-foreground'}`}>{i + 1}</span><span className="font-medium">{m.member_name}</span></span>
                    <span className="text-xs text-muted-foreground">{m.new_patients} friend{m.new_patients === 1 ? '' : 's'} · {m.rewarded} completed · {m.rewarded} IM reward{m.rewarded === 1 ? '' : 's'}</span>
                  </button>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
      {(data.renewal_requests || []).length > 0 && (
        <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/5 p-2.5 space-y-1" data-testid="renewal-requests">
          <p className="text-[10px] uppercase tracking-[0.2em] text-emerald-300">Renewal visits requested · collect $450 &amp; re-enroll</p>
          {data.renewal_requests.map(r => (
            <button key={r.membership_id} type="button" onClick={() => onOpenPatient?.(r.patient_id)} data-testid={`renewal-request-${r.membership_id}`}
              className="w-full flex items-center justify-between gap-2 py-1 text-left hover:bg-emerald-500/10 px-1 rounded transition-colors text-sm">
              <span><span className="font-medium">{r.patient_name}</span>{r.appointment?.start_datetime && <span className="text-muted-foreground"> · {format(parseISO(r.appointment.start_datetime), 'EEE MMM d · h:mm a')}{r.appointment.service ? ` · ${r.appointment.service}` : ''}</span>}</span>
              <span className="text-[11px] text-muted-foreground">requested {fmt(r.requested_at)}</span>
            </button>
          ))}
        </div>
      )}
      {data.expiring.length === 0 ? (
        <p className="text-xs text-muted-foreground">{data.active_members ? 'No unused credits expiring in the next 30 days.' : 'No active members yet — enroll a patient from their chart’s Membership tab.'}</p>
      ) : (
        <div className="divide-y divide-border/30" data-testid="members-expiring-list">
          {data.expiring.map(x => (
            <button key={x.credit_id} type="button" onClick={() => onOpenPatient?.(x.patient_id)} data-testid={`expiring-credit-${x.credit_id}`}
              className="w-full flex items-center justify-between gap-2 py-2 text-left hover:bg-primary/5 px-1 rounded transition-colors">
              <span className="text-sm min-w-0 truncate"><span className="font-medium">{x.patient_name}</span><span className="text-muted-foreground"> · {x.label}</span></span>
              <span className="flex items-center gap-2 shrink-0 text-xs">
                {x.reminder_sent_at && <span className="text-emerald-300 flex items-center gap-1" title={`Reminded ${fmt(x.reminder_sent_at)}`}><CheckCircle2 className="w-3 h-3" />reminded</span>}
                <span className={`px-2 py-0.5 rounded-full border ${x.days_left <= 7 ? 'border-amber-500/50 text-amber-300 bg-amber-500/10' : 'border-border/40 text-muted-foreground'}`}>{x.days_left === 0 ? 'expires today' : `${x.days_left}d left`} · {fmt(x.expires_at)}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const MembershipTab = ({ patient, canManage }) => {
  const { current, history, loading, reload } = useMembership(patient?.id);
  const [sellOpen, setSellOpen] = useState(false);

  const cancel = async () => {
    if (!window.confirm('Cancel this membership? Remaining credits will be voided.')) return;
    try { await axios.post(`${API}/memberships/${current.id}/cancel`); toast.success('Membership cancelled'); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const restore = async (creditId) => {
    try { await axios.post(`${API}/memberships/${current.id}/credits/${creditId}/restore`); toast.success('Credit restored'); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-4" data-testid="membership-tab">
      {loading && !current && <p className="text-sm text-muted-foreground">Loading…</p>}
      {current ? (
        <div className="rounded-xl border border-primary/40 bg-gradient-to-br from-primary/10 via-transparent to-transparent p-4 space-y-4" data-testid="membership-active-card">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-primary flex items-center gap-1.5"><Crown className="w-4 h-4" />Elite Member</p>
              <p className="text-sm mt-1">{fmt(current.start_date)} → {fmt(current.end_date)}{current.in_bonus_period && <Badge variant="outline" className="ml-2 text-[9px] border-emerald-500/50 text-emerald-300">Bonus period · until {fmt(current.bonus_expires_at)}</Badge>}</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">${current.amount_paid.toFixed(2)} paid via {current.payment_method} · sold by {current.sold_by} on {fmt(current.created_at)}</p>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold gold-gradient-text" data-testid="membership-available-count">{current.available_count}</p>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">credit{current.available_count === 1 ? '' : 's'} available now</p>
            </div>
          </div>
          <CreditGrid membership={current} onRestore={restore} canManage={canManage} />
          {current.notes && <p className="text-xs text-muted-foreground italic">{current.notes}</p>}
          <ReferralCard membership={current} />
          {canManage && <div className="flex justify-end"><Button size="sm" variant="ghost" className="text-rose-300 hover:bg-rose-500/10 h-7 text-xs" onClick={cancel} data-testid="membership-cancel-btn">Cancel membership</Button></div>}
        </div>
      ) : (
        <div className="rounded-xl border border-dashed border-primary/30 p-6 text-center space-y-3" data-testid="membership-empty">
          <Crown className="w-8 h-8 text-primary/60 mx-auto" />
          <p className="text-sm text-muted-foreground">No active Elite Membership.</p>
          <p className="text-xs text-muted-foreground">$450 · 3 months · 1 IV drip per month · bonus IV + IM injection at the end of the term.</p>
          {canManage && <Button onClick={() => setSellOpen(true)} className="bg-primary text-primary-foreground" data-testid="sell-membership-btn"><Crown className="w-4 h-4 mr-1" />Enroll in Elite Membership</Button>}
        </div>
      )}
      {history.filter(m => m.status !== 'active').length > 0 && (
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">Past memberships</p>
          <div className="space-y-1.5">
            {history.filter(m => m.status !== 'active').map(m => (
              <div key={m.id} className="text-xs flex items-center justify-between rounded-md border border-border/40 px-3 py-2" data-testid={`membership-history-${m.id}`}>
                <span>{fmt(m.start_date)} → {fmt(m.end_date)} · {m.used_count}/{m.credits.length} credits used</span>
                <Badge variant="outline" className="text-[9px] capitalize">{m.status}</Badge>
              </div>
            ))}
          </div>
        </div>
      )}
      <SellMembershipDialog open={sellOpen} onClose={() => setSellOpen(false)} patient={patient} onSold={reload} />
    </div>
  );
};

const eligible = (credit, svc) => svc && (credit.type === 'bonus_im' ? svc.category === 'injection' : ['iv_drip', 'hydration'].includes(svc.category));

export const CheckoutMembershipSection = ({ patientId, selectedServices, applied, onChange }) => {
  const { current } = useMembership(patientId);
  const [initialized, setInitialized] = useState(false);
  useEffect(() => {
    if (!current) return;
    if (!initialized) {
      const taken = new Set(); const next = [];
      for (const c of current.credits.filter(x => x.state === 'available')) {
        const cand = selectedServices.filter(s => eligible(c, s) && !taken.has(s.id)).sort((a, b) => (b.price || 0) - (a.price || 0))[0];
        if (cand) { next.push({ credit_id: c.id, service_type_id: cand.id }); taken.add(cand.id); }
      }
      onChange(next); setInitialized(true); return;
    }
    const pruned = applied.filter(a => selectedServices.some(s => s.id === a.service_type_id));
    if (pruned.length !== applied.length) onChange(pruned);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, selectedServices]);

  if (!current) return null;
  const available = current.credits.filter(c => c.state === 'available');
  const toggle = (credit) => {
    const isOn = applied.some(a => a.credit_id === credit.id);
    let next;
    if (isOn) next = applied.filter(a => a.credit_id !== credit.id);
    else {
      const taken = new Set(applied.map(a => a.service_type_id));
      const cand = selectedServices.filter(s => eligible(credit, s) && !taken.has(s.id)).sort((a, b) => (b.price || 0) - (a.price || 0))[0];
      if (!cand) { toast.error(`Select an eligible ${credit.type === 'bonus_im' ? 'injection' : 'IV drip'} first`); return; }
      next = [...applied, { credit_id: credit.id, service_type_id: cand.id }];
    }
    onChange(next);
  };

  return (
    <div className="rounded-xl border border-primary/40 bg-gradient-to-r from-primary/15 to-transparent p-3 space-y-2" data-testid="checkout-membership">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-primary uppercase tracking-widest flex items-center gap-1.5"><Crown className="w-4 h-4" />Elite Member · {available.length} credit{available.length === 1 ? '' : 's'} available</p>
        <p className="text-[11px] text-muted-foreground">Term ends {fmt(current.end_date)}</p>
      </div>
      {available.length === 0 && <p className="text-xs text-muted-foreground">No credit available for this visit{current.credits.some(c => c.state === 'upcoming') ? ` — next unlocks ${fmt(current.credits.filter(c => c.state === 'upcoming').sort((a, b) => a.window_start.localeCompare(b.window_start))[0].window_start)}` : ''}.</p>}
      {available.map(c => {
        const a = applied.find(x => x.credit_id === c.id);
        const svc = a && selectedServices.find(s => s.id === a.service_type_id);
        return (
          <button key={c.id} type="button" onClick={() => toggle(c)} data-testid={`apply-credit-${c.id}`}
            className={`w-full text-left flex items-center justify-between gap-2 rounded-lg border px-3 py-2 transition-colors ${a ? 'border-emerald-500/60 bg-emerald-500/10' : 'border-border/40 hover:border-primary/50'}`}>
            <span className="text-sm flex items-center gap-2"><Gift className={`w-4 h-4 ${a ? 'text-emerald-300' : 'text-muted-foreground'}`} />{c.label}{svc && <span className="text-xs text-muted-foreground">→ {svc.name}</span>}</span>
            <span className={`text-sm font-semibold ${a ? 'text-emerald-300' : 'text-muted-foreground'}`}>{a ? `−$${(svc?.price || 0).toFixed(2)}` : 'Apply'}</span>
          </button>
        );
      })}
    </div>
  );
};
